/** @jsxRuntime classic */
/** @jsx jsx */
/** @jsxFrag React.Fragment */
// @ts-check
import Card from '@bottlebooks/gatsby-design-system/src/components/Card/Card';
import {
  Box,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  Container,
  Grid,
  Link,
  Text,
  Title,
} from '@bottlebooks/gatsby-theme-base';
import Layout from '@bottlebooks/gatsby-theme-event/src/components/Layout/Layout';
import { Plural, Trans } from '@lingui/macro';
import { graphql } from 'gatsby';
import React from 'react';
import { jsx } from 'theme-ui';
import FloorPlansSection from '../../components/FloorPlansSection';

export default function FloorPage({ data, pageContext, path }) {
  const { floor, previous, next } = afterQuery(data);
  return (
    <Layout locale={pageContext.locale}>
      <FloorHeader
        floor={floor}
        previous={previous}
        next={next}
        sx={{ backgroundColor: 'light' }}
      />

      <FloorPlansSection
        floorId={floor.slug}
        shadowColor="rgba(0, 0, 0, 0.1)"
        sx={{ backgroundColor: 'nuanced' }}
      />

      <Box sx={{ backgroundColor: 'light', paddingX: 'gutter', paddingY: 3 }}>
        <Grid width={[null, 400]} height={250}>
          {floor.rooms.map((room) => (
            <Room key={room.id} room={room} pathname={path} />
          ))}
        </Grid>
      </Box>
    </Layout>
  );
}

function FloorHeader({ floor, previous, next }) {
  return (
    <Box sx={{}}>
      <Container sx={{ paddingTop: 2 }}>
        <Navigation previous={previous} next={next} />
        <Title
          variant="large"
          sx={{
            textTransform: 'uppercase',
            paddingTop: 4,
            paddingBottom: 1,
            marginTop: 'auto',
          }}
        >
          {floor.name}
        </Title>
      </Container>
    </Box>
  );
}

function Navigation({ previous, next }) {
  return (
    <Grid
      sx={{
        gridTemplateColumns: ['32px 1fr 32px', '1fr 1fr 1fr'],
      }}
    >
      <Text variant="small">
        {previous && (
          <Link to={`/exhibitors/${previous.slug}`}>
            <ChevronLeftIcon />{' '}
            <span sx={{ display: ['none', 'inline'] }}>{previous.name}</span>
          </Link>
        )}
      </Text>
      <Text variant="small" sx={{ textAlign: 'center' }}>
        <Link to={`/exhibitors`}>
          <ChevronUpIcon /> <Trans>All exhibitors</Trans>
        </Link>
      </Text>
      <Text variant="small" sx={{ textAlign: 'right' }}>
        {next && (
          <Link to={`/exhibitors/${next.slug}`}>
            <span sx={{ display: ['none', 'inline'] }}>{next.name}</span>{' '}
            <ChevronRightIcon />
          </Link>
        )}
      </Text>
    </Grid>
  );
}

function Room({ room, pathname }) {
  return (
    <Card
      as={Link}
      to={`${pathname}${room.slug}`}
      sx={{
        gridTemplateRows: '5fr 2fr auto',
        backgroundColor: room.colors?.primary,
        color: room.colors?.onPrimary,
        transition: 'opacity',
        ':hover': {
          color: room.colors?.onPrimary,
          opacity: 0.9,
        },
      }}
    >
      <Title
        variant="small"
        sx={{
          marginTop: 'auto',
          padding: 3,
          paddingBottom: 1,
          textTransform: 'uppercase',
          textAlign: 'right',
        }}
      >
        {room.name}
      </Title>

      <Text
        sx={{
          color: room.colors?.primary,
          textTransform: 'uppercase',
          textAlign: 'right',
          backgroundColor: room.colors?.onPrimary,
          paddingX: 3,
          paddingY: 2,
        }}
      >
        {room.subtitle}
      </Text>
      <Text
        variant="smallest"
        sx={{
          backgroundColor: room.colors?.onPrimary,
          color: 'lighterText',
          paddingX: 3,
          paddingY: 2,
        }}
      >
        {room.standCount ? (
          <Plural
            value={room.standCount}
            one="# exhibitor"
            other="# exhibitors"
          />
        ) : (
          <>&nbsp;</>
        )}
      </Text>
    </Card>
  );
}

export const query = graphql`
  query ($id: String) {
    floor(id: { eq: $id }) {
      id
      name
      slug
      rooms {
        id
        name
        slug
        subtitle
        standCount
        colors {
          onPrimary
          primary
        }
      }
    }
    allFloor {
      edges {
        node {
          id
        }
        next {
          slug
          name
        }
        previous {
          slug
          name
        }
      }
    }
  }
`;

function afterQuery({ floor, allFloor }) {
  const { previous, next } =
    allFloor.edges.find(({ node }) => node.id === floor.id) || {};
  return {
    floor,
    previous,
    next,
  };
}
